import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import axiosInstance from 'utils/axiosUtil';
import { PhoneUtil } from 'doctivity-shared/utils';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControlLabel, Switch, Typography, Box, Stack, Autocomplete, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, MenuItem,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Traditional (non-RJSF) add/edit user dialog, modeled on AnnouncementDialog. Client access and
// per-client territory live together in one table: each row is a client the user works, with a
// territory-market dropdown and (for admins) a remove button, plus an "add client" search.
//   - Regular edited user: the table IS their client access — rows grant access; the ids are sent as
//     `clients` and the per-row markets as `territories`.
//   - Admin edited user: access is all-clients, so the table only sets territories to test; the backend
//     saveClientAccess admin branch ignores `clients` and writes only `territories`.
// A user editing their OWN profile (not an admin) can set territories but cannot add/remove clients or
// change admin/active flags.

const emptyForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  first_name_pronunciation: '',
  is_active: true,
  is_admin: false,
  send_invite: false,
};

const marketLabel = (m) => `${m.name}${m.is_private ? ' (Private)' : ''}`;

function UserEditDialog({ open, userData, clients, user, onSave, onClose }) {
  const isNew = !(userData && userData.id);
  const iAmAdmin = !!(user && user.is_admin);

  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]); // [{ client_id, client_name, territory, markets }]
  const [addClient, setAddClient] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAddClient(null);
    setSaving(false);
    const base = userData || {};
    setForm({
      ...emptyForm,
      first_name: base.first_name || '',
      last_name: base.last_name || '',
      email: base.email || '',
      phone: base.phone ? PhoneUtil.formatNumber(base.phone) : '',
      first_name_pronunciation: base.first_name_pronunciation || '',
      is_active: base.is_active !== false,
      is_admin: !!base.is_admin,
      send_invite: false,
    });
    // Seed client/territory rows from the per-client territory endpoint (existing users only).
    if (base.id) {
      axiosInstance
        .get('/Markets', { params: { territoryFor: base.id } })
        .then((res) => {
          const data = (res.data && Array.isArray(res.data.clients)) ? res.data : { clients: [] };
          setRows(data.clients.map((c) => ({
            client_id: c.client_id,
            client_name: c.client_name,
            territory: c.territory ?? null,
            markets: c.markets || [],
          })));
        })
        .catch(() => setRows([]));
    } else {
      setRows([]);
    }
  }, [open, userData]);

  const shownIds = new Set(rows.map((r) => Number(r.client_id)));
  const addableClients = (Array.isArray(clients) ? clients : []).filter((c) => !shownIds.has(Number(c.id)));

  const setTerritory = (clientId, marketId) => {
    setRows((rs) => rs.map((r) => (
      Number(r.client_id) === Number(clientId)
        ? { ...r, territory: marketId === '' ? null : Number(marketId) }
        : r
    )));
  };

  const removeRow = (clientId) => {
    setRows((rs) => rs.filter((r) => Number(r.client_id) !== Number(clientId)));
  };

  const doAddClient = async () => {
    if (!addClient) return;
    const clientId = Number(addClient.id);
    if (!clientId || shownIds.has(clientId)) { setAddClient(null); return; }
    let markets = [];
    try {
      // territoryFor is the user id, or 0 for a not-yet-created user (backend returns the client's
      // public markets in that case, since a new user has no private markets yet).
      const uid = (userData && userData.id) || 0;
      const res = await axiosInstance.get('/Markets', { params: { territoryFor: uid, client: clientId } });
      markets = (res.data && Array.isArray(res.data.markets)) ? res.data.markets : [];
    } catch (e) { markets = []; }
    setRows((rs) => [...rs, { client_id: clientId, client_name: addClient.name, territory: null, markets }]);
    setAddClient(null);
  };

  const validate = () => {
    if (!form.first_name.trim()) return 'First name is required.';
    if (!form.last_name.trim()) return 'Last name is required.';
    if (!form.email.trim()) return 'Email is required.';
    // Phone is required for non-admin users (matches the previous rule).
    if (!iAmAdmin && !form.phone.trim()) return 'Phone is required.';
    if (form.phone.trim()) {
      const digits = PhoneUtil.unformat(form.phone);
      if (!digits || !PhoneUtil.isValid(digits)) return 'Invalid phone number.';
    }
    // A non-admin user needs at least one client (matches the old dialog's client_ids minItems:1).
    // Only enforced when an admin is managing access — a user editing their own profile can't change it.
    if (iAmAdmin && !form.is_admin && rows.length === 0) {
      return 'Assign at least one client, or make the user a System Administrator.';
    }
    return null;
  };

  // Client-side email-uniqueness check (admins only — they can change emails / create users).
  const emailIsTaken = (email, id) => axiosInstance
    .get('/Users', { params: { opts: { where: { email: { $like: email }, id: { $ne: id ? parseInt(id, 10) : 0 } } } } })
    .then((res) => Array.isArray(res.data) && res.data.length > 0)
    .catch(() => false);

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSaving(true);

    if (iAmAdmin && await emailIsTaken(form.email, userData && userData.id)) {
      setSaving(false);
      setError('A user already exists with this email.');
      return;
    }

    const clientIds = rows.map((r) => Number(r.client_id));
    const territories = {};
    rows.forEach((r) => { territories[r.client_id] = r.territory ?? null; });

    const data = {
      ...(userData && userData.id ? { id: userData.id } : {}),
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() ? PhoneUtil.unformat(form.phone) : null,
      first_name_pronunciation: form.first_name_pronunciation,
      is_active: form.is_active,
      is_admin: form.is_admin,
      send_invite: isNew ? form.send_invite : false,
      clients: clientIds,
      territories,
    };
    // onSave dispatches the upsert and the parent closes the dialog.
    onSave(data);
  };

  const accessNote = form.is_admin
    ? 'This user is a system administrator with access to all clients. Add a client here only to set a territory (e.g. for testing).'
    : (iAmAdmin ? 'Clients this user can access. Remove to revoke, or add below to grant.' : 'Your assigned clients and their territory.');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>{isNew ? 'Add User' : 'Edit User'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label='First Name' fullWidth required value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} />
          <TextField label='Last Name' fullWidth required value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label='Email'
            fullWidth
            required
            type='email'
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
            InputProps={{ readOnly: !iAmAdmin }}
            helperText={!iAmAdmin ? 'Contact an administrator to change your email.' : ''}
          />
          <TextField
            label='Phone'
            fullWidth
            required={!iAmAdmin}
            value={form.phone}
            onChange={(e) => set({ phone: PhoneUtil.formatNumber(e.target.value) })}
          />
        </Box>

        {iAmAdmin && (
          <>
            <TextField
              label='First Name Pronunciation'
              fullWidth
              sx={{ mb: 2 }}
              value={form.first_name_pronunciation}
              onChange={(e) => set({ first_name_pronunciation: e.target.value })}
            />
            <Stack direction='row' spacing={3} sx={{ mb: 1 }}>
              <FormControlLabel
                control={<Switch checked={form.is_active} onChange={(e) => set({ is_active: e.target.checked })} />}
                label='Active'
              />
              <FormControlLabel
                control={<Switch checked={form.is_admin} onChange={(e) => set({ is_admin: e.target.checked })} />}
                label='System Administrator (access to ALL clients)'
              />
              {isNew && (
                <FormControlLabel
                  control={<Switch checked={form.send_invite} onChange={(e) => set({ send_invite: e.target.checked })} />}
                  label='Send invitation email'
                />
              )}
            </Stack>
          </>
        )}

        {/* Client access + per-client territory */}
        <Box sx={{ mt: 2 }}>
          <Typography variant='subtitle2' gutterBottom>Client Access &amp; Territory</Typography>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
            {accessNote}
          </Typography>

          <TableContainer component={Paper} variant='outlined' sx={{ maxHeight: 300, width: '100%' }}>
              <Table size='small' stickyHeader sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '50%' }}>Territory Market</TableCell>
                    {iAmAdmin && <TableCell sx={{ width: 48 }} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={iAmAdmin ? 3 : 2}>
                        <Typography variant='body2' color='text.secondary'>
                          {form.is_admin ? 'No territories set.' : 'No clients assigned yet.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : rows.map((r) => (
                    <TableRow key={r.client_id} hover>
                      <TableCell>{r.client_name || `Client ${r.client_id}`}</TableCell>
                      <TableCell>
                        <TextField
                          select
                          fullWidth
                          size='small'
                          variant='standard'
                          value={(r.territory === null || r.territory === undefined) ? '' : r.territory}
                          onChange={(e) => setTerritory(r.client_id, e.target.value)}
                        >
                          <MenuItem value=''><em>(No territory)</em></MenuItem>
                          {r.markets
                            .filter((m) => m && (m.id === 0 || m.id))
                            .map((m) => (
                              <MenuItem key={m.id} value={m.id}>{marketLabel(m)}</MenuItem>
                            ))}
                        </TextField>
                      </TableCell>
                      {iAmAdmin && (
                        <TableCell>
                          <Tooltip title={form.is_admin ? 'Remove territory' : 'Revoke access'}>
                            <IconButton size='small' onClick={() => removeRow(r.client_id)}>
                              <DeleteOutlineIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

          {iAmAdmin && addableClients.length > 0 && (
            <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 2 }}>
              <Autocomplete
                size='small'
                options={addableClients}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
                value={addClient}
                onChange={(e, v) => setAddClient(v)}
                sx={{ minWidth: 280 }}
                renderInput={(params) => <TextField {...params} label={form.is_admin ? 'Add a client to set a territory' : 'Add a client'} />}
              />
              <Button variant='outlined' size='small' disabled={!addClient} onClick={doAddClient}>Add</Button>
            </Stack>
          )}
        </Box>

        {error && <Typography color='error' sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>Cancel</Button>
        <Button onClick={handleSave} variant='contained' color='primary' disabled={saving}>
          {isNew ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UserEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  clients: PropTypes.array,
  userData: PropTypes.object,
  user: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return { user: state.user };
}

const connected = connect(mapStateToProps)(UserEditDialog);
export { connected as UserEditDialog };
export default connected;
